import { Component, type ReactNode, type ErrorInfo } from 'react';
import { resetBodyOverflow } from '@/hooks/useModalOverflow';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Crash capturado:', error, info);
    // Resetar body overflow em caso de crash de modal — evita ecrã "preto"
    resetBodyOverflow();
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-error-warning-line text-red-500 text-2xl" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Algo correu mal</h2>
            <p className="text-sm text-slate-500 mb-6">
              Ocorreu um erro inesperado. Tenta recarregar a página.
            </p>
            {this.state.error && (
              <p className="text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2 mb-5 font-mono text-left break-all">
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={this.handleReload}
              className="whitespace-nowrap flex items-center gap-2 mx-auto px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              Recarregar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
